import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Avatar,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  Divider,
} from '@mui/material';
import api from '../../api';
import SecuritySettings from './SecuritySettings';
import GeneralSettings from './GeneralSettings';
import AccountDeletion from './AccountDeletion';
import ShimmerLoader from '../ShimmerLoader';
import { getAvatarUrl } from '../../pages/utils/avatarCache';

const AccountSettings = () => {
  const [tabValue, setTabValue] = useState(0);
  const [user, setUser] = useState(() => {
          const storedUser = localStorage.getItem("User");
        
          return storedUser ? JSON.parse(storedUser) : {};
      });    
  const [loading, setLoading] = useState(false);
  const token = JSON.parse(localStorage.getItem("token")).token;
  const [organisation, setOrganisation] = useState({});
  const id = user.Organisation;
  const [avatarUrl, setAvatarUrl] = useState('');

  const date = new Date(user.CreatedAt);
  const handleUserUpdate = () => {
    // Call the getUser function
    getUser();
    // Reload the current tab
    window.location.reload();
  };
  const formattedDateTime = new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
    timeZone: "UTC", // Change this to user's preferred timezone if needed
    timeZoneName: "short" // Gets the timezone abbreviation
  }).format(date)
    .replace(",", "") // Remove comma
    .replace(/(am|pm)/i, match => match.toUpperCase()); // Capitalize AM/PM
    
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Function to get appropriate avatar URL using the utility function
  const handleGetAvatarUrl = useCallback(() => {
    return getAvatarUrl(user);
  }, [user?.ProfilePicture]);
 
  const getUser = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/users/profile`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setUser(response.data.user);
    } catch (error) {
      console.error("Error fetching phone:", error);
    }
    finally {
      setLoading(false);
    }
  }   

  const getCompanyById = useCallback(async () => {
    try {
        const res = await api.get(`/organizations/${id}`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        setOrganisation(res.data);

    } catch (error) {
        console.log(error);
    }
    }, [id, token]);

    useEffect(() => {
        if (token) {
            getCompanyById();
        }
    }, [token]);
    
    // Update avatar URL when user data changes
    useEffect(() => {
        setAvatarUrl(handleGetAvatarUrl());
    }, [user, handleGetAvatarUrl]);

    // Listen for storage changes to update avatar if default avatar is cached
    useEffect(() => {
        const handleStorageChange = () => {
            setAvatarUrl(handleGetAvatarUrl());
        };
        
        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, [handleGetAvatarUrl]);
    
    const userFields = [
        { label: "Role", value: user.Role || "Not Assigned" },
        { label: "Email address", value: user.Email || "Not Found" },
        { label: "Phone", value: user.Phone || "Not Found" },
        { label: "Organisation Name", value: organisation?.Name || "No Organisation Assigned" },
      ];
    
    const tabData = [
      { label: 'General' },
      { label: 'Security' },
      { label: 'Account Deletion' },
    ];

  const LeftSideInfo = () => (
    <Box sx={{ bgcolor: "grey.50",
            height: '100%', 
            p: 3, 
            borderTopLeftRadius: '12px',
            borderBottomLeftRadius: '12px',
            borderTopRightRadius: '0px',
            borderBottomRightRadius: '0px', }}>
            
      {/* Logo Section  */}
      <Box 
        sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'center', 
          mb: 4 
        }}
      >
        <Avatar
          src={avatarUrl}
          alt="Profile Picture"
          sx={{ 
            width: 80, 
            height: 80, 
            mb: 2 
          }}
        />
       <Box sx={{ textAlign: 'center'}}>
  <Typography 
    variant="h6"
    sx={{
      fontSize: '1.25rem',
      fontWeight: 600,
      color: '#2c3e50',
      mb: 0.5
    }}
  >
    {user.Name}
  </Typography>
  <Typography 
    sx={{
      fontSize: '0.9rem',
      color: '#64748b',
      fontWeight: 500
    }}
  >
    Onboarded at: {formattedDateTime}
  </Typography>
</Box>

      </Box>

      <Divider />

      {/* User Information */}
      <List sx={{ padding: 0 }}>
  {userFields.map((field, index) => (
    <ListItem key={index} sx={{ paddingY: 0.7 }}> 
      <ListItemText
        primary={
          <Typography 
            sx={{
              fontSize: '1.1rem',
              fontWeight: 600,
              color: '#2c3e50',
              mb: 0.3 // Reduce bottom margin
            }}
          >
            {field.label}
          </Typography>
        }
        secondary={
          <Typography 
            sx={{
              fontSize: '0.875rem',
              color: '#64748b',
              lineHeight: 1.3 // Reduce line height
            }}
          >
            {field.value}
          </Typography>
        }
      />
    </ListItem>
  ))}
</List>


</Box>
  );

  if(loading){
    return <ShimmerLoader/>
  }
  
  return (
    <Box sx={{ display:'flex', bgcolor: '#ffffff', borderColor: '#E5E7EB', height:'auto', borderWidth: '1px', borderStyle: 'solid',borderRadius: '12px' }}>
      <Box sx={{ width: '30%', borderRight: '1px solid #e0e0e0' }}>
        <LeftSideInfo />
      </Box>
      <Box sx={{ width: '70%', bgcolor: 'white', p: 3, overflowY: 'auto',borderTopRightRadius: '12px',
            borderBottomRightRadius: '12px',  }}>
      <Tabs
        value={tabValue}
        onChange={handleTabChange}
        sx={{
          ml:-2,
          mb: 2,
          "& .MuiTabs-indicator": {
            display: "none",
          },
          "& .MuiTab-root": {
            minWidth: "auto",
            minHeight: "auto",
            padding: "8px 16px",
            color: "#6B7280",
            "&.Mui-selected": {
              color: "#111827",
              bgcolor: "#EDEDED",
              borderRadius: "6px",
            },
          },
        }}
      >
          {tabData.map((tab, index) => (
          <Tab
            key={index}
            label={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <span style={{
                  textTransform: 'none',
                  fontSize: '1rem',
                }}>{tab.label}</span>
              </Box>
            }
            sx={{
              ml: 2,
              "&.Mui-selected": {
                "& .MuiSvgIcon-root": {
                  color: "primary.main",
                },
              },
            }}
          />
        ))}
      </Tabs>

        {/* General Settings */}
        {tabValue === 0 && (
          <GeneralSettings onUserUpdate={handleUserUpdate} />
        )}

        {/* Security Settings */}
        {tabValue === 1 && (
          <SecuritySettings />
        )}

        {/* Account Deletion */}
        {tabValue === 2 && (
          <AccountDeletion />
        )}
       
      </Box>
    </Box>
  );
};

export default AccountSettings;